import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const isAuthCall = original?.url?.startsWith("/auth/");
    if (error.response?.status === 401 && !isAuthCall && !original._retry) {
      original._retry = true;
      refreshing = refreshing || api.post("/auth/refresh").finally(() => (refreshing = null));
      try {
        await refreshing;
        return api(original);
      } catch {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e?.msg ? `${(e.loc || []).filter((l) => l !== "body").join(".")}: ${e.msg}` : JSON.stringify(e)))
      .join(" · ");
  return detail.msg || String(detail);
}

export const authLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);
export const authMe = () =>
  api.get("/auth/me").catch(async (e) => {
    if (e.response?.status !== 401) throw e;
    await api.post("/auth/refresh");
    return api.get("/auth/me");
  }).then((r) => r.data);
export const authLogout = () => api.post("/auth/logout").then((r) => r.data);
export const authChangePassword = (current_password, new_password) =>
  api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);

export const adminList = (kind, params = {}) =>
  api.get(`/admin/${kind}`, { params }).then((r) => r.data);
export const adminGet = (kind, id) =>
  api.get(`/admin/${kind}/${id}`).then((r) => r.data);
export const adminCreate = (kind, data) =>
  api.post(`/admin/${kind}`, data).then((r) => r.data);
export const adminUpdate = (kind, id, data) =>
  api.put(`/admin/${kind}/${id}`, data).then((r) => r.data);
export const adminDelete = (kind, id) =>
  api.delete(`/admin/${kind}/${id}`).then((r) => r.data);

export const analyticsStatus = () => api.get("/analytics/status").then((r) => r.data);
export const googleConnectStart = async () => {
  await api.post("/auth/refresh");
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  return api.post("/analytics/google/start", { origin: window.location.origin }).then((r) => r.data);
};
export const googleDisconnect = () => api.post("/analytics/google/disconnect").then((r) => r.data);
export const analyticsRealtime = () => api.get("/analytics/realtime").then((r) => r.data);
export const analyticsSummary = () => api.get("/analytics/summary").then((r) => r.data);
export const analyticsBreakdown = (days) =>
  api.get("/analytics/breakdown", { params: { days } }).then((r) => r.data);

export const teamList = () => api.get("/team").then((r) => r.data);
export const teamAdd = (data) => api.post("/team", data).then((r) => r.data);
export const teamRemove = (id) => api.delete(`/team/${id}`).then((r) => r.data);

export const fetchProducts = (params = {}) =>
  api.get("/products", { params }).then((r) => r.data);
export const fetchProduct = (slug) =>
  api.get(`/products/${slug}`).then((r) => r.data);

export const fetchArticles = (params = {}) =>
  api.get("/articles", { params }).then((r) => r.data);
export const fetchArticle = (slug) =>
  api.get(`/articles/${slug}`).then((r) => r.data);

export const fetchComparisons = (params = {}) =>
  api.get("/comparisons", { params }).then((r) => r.data);
export const fetchComparison = (slug) =>
  api.get(`/comparisons/${slug}`).then((r) => r.data);

export const fetchCategories = () =>
  api.get("/categories").then((r) => r.data);
export const fetchCategory = (slug) =>
  api.get(`/categories/${slug}`).then((r) => r.data);

export const searchAll = (q) =>
  api.get("/search", { params: { q } }).then((r) => r.data);

export const submitContact = (payload) =>
  api.post("/contact", payload).then((r) => r.data);
